<template>
	<v-dialog v-if="length(invoices) && auth.user_type === 1" v-model="dialog" max-width="800px" transition="scroll-x-reverse-transition">
		<v-card>
			<v-card-title>
				<span class="text-h5">Send Invoice Statement</span>
			</v-card-title>
			<v-card-text>
				<v-container class="mt-3">
					<v-row>
						<v-col>
							<v-text-field
								v-model="form.recipient_name"
								name="recipient_name"
								label="Recipient Name"
								outlined
								persistent-placeholder
								dense
								:error="hasErrors(form, 'recipient_name')"
								:error-messages="errorMsg(form, 'recipient_name')"
								:hide-details="! hasErrors(form, 'recipient_name')"
								required />
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<v-text-field
								v-model="form.recipient_email"
								name="recipient_email"
								label="Recipient Email"
								type="email"
								outlined
								persistent-placeholder
								dense
								:error="hasErrors(form, 'recipient_email')"
								:error-messages="errorMsg(form, 'recipient_email')"
								:hide-details="! hasErrors(form, 'recipient_email')"
								required />
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<v-text-field
								v-model="form.subject"
								name="subject"
								label="Subject"
								outlined
								persistent-placeholder
								dense
								:error="hasErrors(form, 'subject')"
								:error-messages="errorMsg(form, 'subject')"
								:hide-details="! hasErrors(form, 'subject')"
								required />
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<v-textarea
								v-model="form.message"
								name="message"
								outlined
								label="Your Message"
								autofocus
								persistent-placeholder
								dense
								:error="hasErrors(form, 'message')"
								:error-messages="errorMsg(form, 'message')"
								:hide-details="! hasErrors(form, 'message')"
								required />
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<fieldset>
								<legend>Message Preview</legend>
								<div class="pa-4">
									<p>Hi {{ form.recipient_name }},</p>

									<p v-if="form.message">{{ form.message }}</p>

									<v-sheet outlined class="mb-3">
										<v-simple-table>
											<template #default>
												<thead>
													<tr>
														<th style="white-space: nowrap">Invoice #</th>
														<th>Amount</th>
														<th>Project</th>
														<th>Date</th>
														<th>Age</th>
													</tr>
												</thead>
												<tbody>
													<tr
														v-for="invoice in invoices"
														:key="`invoice-statment-` + invoice.id">
														<td>
															<a :href="route('public.invoices.show', invoice)" target="_blank">
																#{{ invoice.id }}
															</a>
														</td>
														<td>{{ invoice.total_due | money }}</td>
														<td>{{ invoice.project.name }} &nbsp;&nbsp;<em>(#{{ invoice.project.id }})</em></td>
														<td>{{ invoice.created_at | localDate }}</td>
														<td>{{ invoice.aging }} days</td>
													</tr>
												</tbody>
											</template>
										</v-simple-table>
									</v-sheet>

									<p>Thanks,</p>
									<p>
										{{ auth.name }}<br />
										{{ settings.billing_name }}<br />
										{{ settings.billing_address1 }}<br />
										<span v-if="settings.billing_city && settings.billing_state_id && settings.billing_zipcode">
											{{ settings.billing_city }},
											{{ getState(settings.billing_state_id) }} {{ settings.billing_zipcode }}
										</span><br />
										{{ settings.billing_phone }}
									</p>
								</div>
							</fieldset>
						</v-col>
					</v-row>
					<v-row>
						<v-col>
							<div><strong>Attachment(s):</strong></div>
							<div v-for="(filename, index) in filenames" :key="`invoice-file-` + index">
								{{ filename }}
							</div>
						</v-col>
					</v-row>
				</v-container>
			</v-card-text>
			<v-divider />
			<v-card-actions>
				<v-spacer></v-spacer>
				<v-btn
					color="warning darken-1"
					text
					:disabled="form.processing"
					@click="cancel">
					Cancel
				</v-btn>
				<v-btn
					color="success"
					:loading="form.processing"
					:disabled="!isValid || form.hasErrors"
					@click="sendInvoiceStatement">
					Send Email
				</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>
</template>
<script>
import states from '@/Mixins/states'
import api from '@/api'

export default {
	mixins: [ states ],
	props: {
		value: {
			type: Boolean,
			default: null
		},
		invoices: {
			type: Array,
			default: () => [],
			required: true
		}
	},
	data () {
		return {
			auth: this.$page.props.auth,
			dialog: this.value,
			settings: this.$page.props.settings,
			project: this.$page.props.project,
			form: this.$inertia.form({
				invoices: [],
				recipient_name: '',
				recipient_email: '',
				subject: 'Bell Oldow invoice statement',
				message: ''
			}),
			filenames: []
		}
	},
	computed: {
		isValid () {
			return this.form.recipient_name !== ''
				&& this.form.recipient_email !== ''
				&& this.form.message !== ''
				&& this.length(this.invoices) > 0
		},

		/**
		 * Returns the client billing contact information.
		 * Attempts to pull this from the invoices or the page props.
		 *
		 * @returns {*|{billing_contact_name: string, billing_contact_email: string}}
		 */
		client () {
			if (this.length(this.invoices) && this.invoices[0].project.hasOwnProperty('client')) {
				return this.invoices[0].project.client
			}

			if (this.project.hasOwnProperty('client')) {
				return this.project.client
			}

			return {
				billing_contact_name: '',
				billing_contact_email: ''
			}
		}
	},
	watch: {
		dialog (newValue) {
			this.$emit('input', newValue)
			this.populateForm()

			if(newValue) {
				this.generateFileNames()
			}
		},

		value (newValue) {
			this.dialog = newValue
		}
	},
	methods: {
		cancel () {
			this.form.reset()
			this.dialog = false
		},

		sendInvoiceStatement () {
			const route = this.route('invoices.send.statement')

			// Simplify invoices array to only be the invoice Ids
			this.form.invoices = this.invoices.map(invoice => invoice.id)

			this.form.post(route, {
				only: ['flash', 'errors'],
				preserveScroll: true,
				preserveState: true,
				onSuccess: () =>  {
					this.dialog = false
					this.form.reset()
				}
			})
		},

		populateForm () {
			this.filenames = []
			this.form.reset()

			if (this.client) {
				this.form.recipient_name = this.client.billing_contact_name
				this.form.recipient_email = this.client.billing_contact_email
			}
		},

		generateFileNames () {
			const method = 'POST'
			const data = {'invoices': this.invoices}

			const url = this.route('generate-invoice-filename')

			api({
				method,
				url,
				data
			}).then(response => {
				this.filenames = response.data
			})
		}
	}
}
</script>
